import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'

interface BulkQuerySummary {
  query_id: string
  question_text: string
  twins_used: number
  ensemble_consensus: number
  status: string
  started_at: string
}

const DEMO_QUERIES: BulkQuerySummary[] = [
  {
    query_id: 'bq_pref_center_demo',
    question_text: 'Which preference-center category framing reduces opt-outs most?',
    twins_used: 1089,
    ensemble_consensus: 0.84,
    status: 'completed',
    started_at: '2026-06-08T14:22:00Z',
  },
]

export function BulkQueriesListPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [queries, setQueries] = useState<BulkQuerySummary[]>([])
  const [loading, setLoading] = useState(true)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    if (isDemo) { setQueries(DEMO_QUERIES); setLoading(false); return }
    fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries`, { headers })
      .then(r => r.ok ? r.json() as Promise<{ queries: BulkQuerySummary[] }> : { queries: [] }).catch(() => ({ queries: [] }))
      .then(d => setQueries(d.queries ?? []))
      .finally(() => setLoading(false))
  }, [projectId, isDemo])

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading...</div>

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium text-deep-ink">Bulk queries</h1>
          <p className="text-sm text-slate-500 mt-0.5">Questions asked across the entire twin panel</p>
        </div>
        <Link to={`/app/projects/${projectId ?? 'demo'}/twin-panel/queries/new`}>
          <Button variant="primary" size="sm" className="gap-2" data-testid="primary-cta">
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            New bulk query
          </Button>
        </Link>
      </div>

      {queries.length === 0 ? (
        <div className="border border-dashed border-border-gray rounded-xl p-8 text-center">
          <p className="text-sm text-slate-400">No bulk queries yet.</p>
          <Link to={`/app/projects/${projectId ?? 'demo'}/twin-panel/queries/new`} className="mt-3 inline-block">
            <Button variant="primary" size="sm">Run your first query</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {queries.map(q => (
            <Link
              key={q.query_id}
              to={`/app/projects/${projectId ?? 'demo'}/twin-panel/queries/${q.query_id}`}
              className="block"
            >
              <Card className="hover:border-echo-indigo/30 cursor-pointer">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 min-w-0">
                    <p className="text-sm font-medium text-deep-ink truncate">{q.question_text}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span>{q.twins_used.toLocaleString()} twins</span>
                      <span>Agreement: {(q.ensemble_consensus * 100).toFixed(0)}%</span>
                      <span>{new Date(q.started_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <Badge variant={q.status === 'completed' ? 'success' : q.status === 'running' ? 'info' : 'warning'} size="sm">
                    {q.status}
                  </Badge>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
